import type {
  AbstractPartDefinition,
  Blueprint,
  BlueprintRenderer,
  DragConstraint,
  DragUpdateOptions,
  DragUpdateResult,
  PartRenderer,
  SnapAnchor,
} from "@project/emerge-types";
import { type ThreeEvent, useThree } from "@react-three/fiber";
import { createElement, memo, useLayoutEffect, useMemo } from "react";
import { Vector3 } from "three";
import { DraggablePart } from "../components/DraggablePart";
import { usePartMenuStore } from "../stores/partMenuStore";
import { useProductStoreContext } from "./ProductStoreContext";
import { useCameraControls } from "./ProductViewerCamera";
import { useProductViewerConfig } from "./ProductViewerConfigContext";
import { resolvePartRaycast } from "./partRaycast";
import { PART_ID_USER_DATA_KEY } from "./productViewerCameraFit";
import { markProductSceneChanged } from "./sceneActivity";
import { useIsLidGroupHidden } from "./useProductViewerGroupInteractions";

export const BlueprintRendererComponent = ({
  blueprint,
  renderer,
}: {
  blueprint: Blueprint;
  renderer: BlueprintRenderer | undefined;
}) => {
  const useProductStore = useProductStoreContext();
  // Subscribe to this renderer's own visibility slice so toggling unrelated
  // visibility (e.g. measurements) does not re-render the blueprint.
  const isHidden = useProductStore((state) =>
    state.hiddenPartTypes.includes(blueprint.type),
  );
  if (isHidden || !renderer) return null;
  return createElement(renderer, { blueprint, key: "blueprint" });
};

export type DragDisplayValues = DragUpdateResult<unknown>["displayValues"];

export type DragOnMoveResult = Pick<
  DragUpdateResult<unknown>,
  "displayValues" | "snappedPosition"
>;

/** Placement position: for PianoHinge use positions[0], otherwise part.position. */
function getPartPlacementPosition(
  part: AbstractPartDefinition<string>,
): Vector3 {
  if (
    part.type === "PianoHinge" &&
    "positions" in part &&
    Array.isArray((part as { positions?: Vector3[] }).positions) &&
    (part as { positions: Vector3[] }).positions.length > 0
  ) {
    const positions = (part as { positions: Vector3[] }).positions;
    const p = positions[0];
    if (p) return new Vector3(p.x, p.y, p.z);
  }
  if ("position" in part && part.position) {
    return part.position;
  }
  return new Vector3(0, 0, 0);
}

/**
 * A static part swallows its pointer events so it shadows whatever sits
 * behind it. The orbit point is not set here: `ProductViewerCamera` picks it
 * from the nearest surface for every part alike.
 */
const stopPartEventPropagation = (event: ThreeEvent<PointerEvent>) => {
  event.stopPropagation();
};

/**
 * One part of the product tree.
 *
 * Memoized, and that is the point: a build hands back the *same* part object
 * for every part whose type no interaction declares as mutated, so a resize
 * that moves the panels leaves the corners, locks and handle untouched by
 * identity. Without this every one of them re-rendered on every build of a
 * drag, and a CPU profile of a real gesture put React element creation at
 * about a third of all non-idle time. The mechanism is
 * `AbstractProduct.preserveCachedPart`, which clones a part only when
 * `AbstractPartInteraction.mutatedPartTypes` names its type — so the saving is
 * the parts no interaction touches, not the panels and pads, which are
 * declared mutable and still arrive as new objects.
 *
 * It only works while every prop keeps its identity — `onDragUpdate` is held
 * stable through latest-value refs in `ProductViewerXr` for exactly this
 * reason. A part that genuinely changed arrives as a new object and re-renders
 * as before; a build that rewrites a *preserved* part in place does not, and
 * `AbstractProduct.cloneCutoutArraysForReact` is the one place that does.
 */
export const PartRendererComponent = memo(function PartRendererComponent({
  part,
  renderer,
  group,
  onDragUpdate,
}: Readonly<{
  part: AbstractPartDefinition<string>;
  renderer: PartRenderer | undefined;
  group: string;
  onDragUpdate?: (
    partId: string,
    position: Vector3,
    constraint: DragConstraint,
    settingsContext: Record<string, unknown> | undefined,
    options?: DragUpdateOptions,
  ) => DragOnMoveResult | undefined;
}>) {
  const cameraControls = useCameraControls();
  const { interaction } = useProductViewerConfig();
  const scene = useThree((state) => state.scene);
  useLayoutEffect(() => {
    markProductSceneChanged(scene);
  });
  useLayoutEffect(() => () => markProductSceneChanged(scene), [scene]);
  const useProductStore = useProductStoreContext();
  // Each part subscribes to only its own type's visibility, so toggling a
  // different type (or the measurement overlay) does not re-render this part.
  const isTypeHidden = useProductStore((state) =>
    state.hiddenPartTypes.includes(part.type),
  );
  // A part of a hidden lid group is hidden for hit testing and drag
  // affordances too: the group is only `visible={false}`.
  const isLidHidden = useIsLidGroupHidden();
  const isHidden = isTypeHidden || isLidHidden;
  const placementPosition = getPartPlacementPosition(part);

  // Names the part on its wrapper group, so tooling and tests can find its
  // geometry by id in the scene. Memoized: r3f re-applies a fresh literal on
  // every render. Left off a part without an id, which the tree
  // keys by type - there is nothing to find it by.
  const partUserData = useMemo(
    () => (part.id ? { [PART_ID_USER_DATA_KEY]: part.id } : undefined),
    [part.id],
  );

  if (!renderer) return null;

  // Wrapping the renderer keeps the hit-test opt-out below *every* handler
  // react-three-fiber raycasts from: the static group here, `DraggablePart`'s
  // own group (which always carries an `onClick`), the lid's `InteractiveGroup`
  // and the camera's orbit-point group. r3f starts its raycast at the handler
  // object, so an override on an outer group would simply never be consulted.
  const partContent = (
    <group
      key={`renderer-${part.id || part.type}-${group}`}
      raycast={resolvePartRaycast(isHidden)}
    >
      {createElement(renderer, { id: part.id || "" })}
    </group>
  );

  // A host can narrow which constrained parts are actually interactive (e.g. to
  // keep corner resizes out of a guided configurator). Excluded parts fall
  // through to the static branch below: no drag, no hover affordance, no click.
  const isDraggable =
    part.dragConstraint !== undefined &&
    (interaction?.isPartDraggable?.(part) ?? true);

  // Activation is resolved per part, so a host can arm one group of parts for
  // plain-press dragging while the rest keep the ⌘/Ctrl gate.
  const dragActivation =
    typeof interaction?.dragActivation === "function"
      ? interaction.dragActivation(part)
      : interaction?.dragActivation;

  // The host's attention and selection, both independent of the activation:
  // the row of one part hovered in a list makes that part glow, and the part
  // being worked on carries the arrows.
  const pointedOut = interaction?.isPartPointedOut?.(part) ?? false;
  const selected = interaction?.isPartSelected?.(part) ?? false;

  const interactionInfo = () => ({
    partId: part.id,
    partType: part.type,
    buildstepName: part.buildstepName,
    sourceOptionUuid: part.dragConstraint?.sourceOptionUuid ?? "",
  });

  if (isDraggable && part.dragConstraint && onDragUpdate) {
    return (
      <group
        visible={!isHidden}
        key={`part-${part.id || part.type}-${group}`}
        userData={partUserData}
      >
        <DraggablePart
          partId={part.id}
          position={placementPosition}
          rotation={part.rotation}
          constraint={part.dragConstraint}
          debug={false}
          controlled={true}
          // Hiding a part type only sets `visible={false}`, and the raycaster
          // ignores that (see `partRaycast.ts`). The part's own renderer opts
          // out of hit testing below, but the drag affordances are siblings of
          // it - and the arrows now carry an invisible grab tube each, which
          // would sit in mid-air catching presses meant for whatever is behind
          // the hidden part. Disabling the part drops every one of them.
          enabled={!isHidden}
          dragActivation={dragActivation}
          pointedOut={pointedOut}
          selected={selected}
          onHoverChange={
            interaction?.onPartHover
              ? (hovered) =>
                  interaction.onPartHover?.(hovered ? interactionInfo() : null)
              : undefined
          }
          onDragStart={() => {
            interaction?.onPartDragStart?.(interactionInfo());
            return cameraControls?.disableControls();
          }}
          onActivate={() => {
            const optionUuid = part.dragConstraint?.sourceOptionUuid;
            if (optionUuid) {
              usePartMenuStore.getState().requestPartMenu(optionUuid);
            }
            interaction?.onPartClick?.(interactionInfo());
          }}
          onDrag={(newPosition: Vector3, options?: DragUpdateOptions) => {
            if (part.dragConstraint && onDragUpdate) {
              return onDragUpdate(
                part.id,
                newPosition,
                part.dragConstraint,
                part.settingsContext,
                options,
              );
            }
          }}
          onDragEnd={(
            finalPos: Vector3,
            _snappedAnchor?: SnapAnchor,
            options?: DragUpdateOptions,
          ) => {
            if (part.dragConstraint) {
              onDragUpdate(
                part.id,
                finalPos,
                part.dragConstraint,
                part.settingsContext,
                options,
              );
            }
          }}
        >
          {partContent}
        </DraggablePart>
      </group>
    );
  }

  // The handlers below deliberately swallow the event: a solid part has to
  // shadow whatever sits behind it, or a draggable part hidden inside the case
  // would answer to a pointer resting on the panel in front of it. Hidden parts
  // must not do that, which `visible={false}` alone does not achieve - see
  // `resolvePartRaycast`, applied to the renderer wrapper above.
  return (
    <group
      position={[placementPosition.x, placementPosition.y, placementPosition.z]}
      rotation={[part.rotation.x, part.rotation.y, part.rotation.z]}
      visible={!isHidden}
      key={`part-${part.id || part.type}-${group}`}
      userData={partUserData}
      onPointerDown={stopPartEventPropagation}
      onPointerEnter={stopPartEventPropagation}
      onPointerLeave={stopPartEventPropagation}
    >
      {partContent}
    </group>
  );
});
